import React, { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useThrottle, useRequest } from "ahooks";
import { useStores } from "@/store";
import { SearchIcon, CloseIcon, LocationIcon } from "@/icons";
import cn from "classnames";
import DcLoading from "@/components/dc-loading";
import { EmptyIcon } from "@/icons";

import "./mobile.less";

const BackIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.87742 8.36962L10.3123 2.27344L11.6878 3.72534L5.25291 9.82153C4.73119 10.3158 4.3393 10.6886 4.04606 10.9994H23V12.9994H4.04606C4.3393 13.3102 4.73119 13.683 5.2529 14.1772L11.6878 20.2734L10.3123 21.7253L3.82804 15.5824C3.15121 14.9412 2.57002 14.3907 2.16734 13.8888C1.73678 13.3521 1.40808 12.7496 1.40808 11.9994C1.40808 11.2492 1.73678 10.6467 2.16734 10.11C2.57002 9.60808 3.15121 9.05753 3.82805 8.41639C3.84445 8.40085 3.8609 8.38527 3.87742 8.36962Z"
      fill="#252525"
    />
  </svg>
);

const noop = () => {};
const MobileRegionPicker = ({
  regions = [],
  setShowRegionPicker = noop,
  onCityClick = noop,
  currentCity = null,
  pageCity = null,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const query = useThrottle(searchValue, { wait: 300 });
  const { CommonStore } = useStores();
  const [isEmpty, setIsEmpty] = useState(false);
  const { t } = useTranslation();

  const { loading, data } = useRequest(
    () => {
      setIsEmpty(false);
      return CommonStore.searchRegion(query);
    },
    {
      refreshDeps: [query],
      loadingDelay: 300,
      onSuccess: (data) => {
        setIsEmpty(data?.headers?.total_count === 0);
      },
    }
  );

  const regionGroups = useMemo(() => {
    if (!Array.isArray(regions)) return [];

    const all = {};
    regions.forEach((item) => {
      const byLetter = item && item.regions;
      if (!byLetter || typeof byLetter !== "object") return;

      Object.keys(byLetter).forEach((letter) => {
        const nextCities = Array.isArray(byLetter[letter])
          ? byLetter[letter]
          : [];
        const prevCities = Array.isArray(all[letter]) ? all[letter] : [];
        all[letter] = prevCities
          .concat(nextCities)
          .slice()
          .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
      });
    });

    return Object.entries(all).sort(([a], [b]) => a.localeCompare(b));
  }, [regions]);

  return (
    <div className="ui-region-picker-mobile">
      <div className="ui-region-picker-mobile__head">
        <span
          className="ui-region-picker-mobile__backBtn"
          onClick={() => setShowRegionPicker(false)}
        >
          <BackIcon />
        </span>
        <div
          className={cn("ui-mobile-search__input", { active: !!searchValue })}
        >
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={t("Header.Search cities")}
          />
          <div className="ui-mobile-search__inputIcon">
            <SearchIcon />
          </div>
          {!!searchValue && (
            <div
              className="ui-mobile-search__clearIcon"
              onClick={() => setSearchValue("")}
            >
              <CloseIcon />
            </div>
          )}
        </div>
      </div>
      <div className="ui-region-picker-mobile__body">
        {!!searchValue ? (
          <>
            <SearchResult
              searchResults={data || []}
              loading={loading}
              isEmpty={isEmpty}
              onCityClick={onCityClick}
            />
            {data.length === 0 && (
              <div className="ui-head-bar__keywordEmpty" role="status" style={{marginTop: 100}}>
                <EmptyIcon />
                <p> {t("Header.No results found")} </p>
              </div>
            )}
          </>
        ) : (
          <RecommendedCities
            regionGroups={regionGroups}
            onCityClick={onCityClick}
            currentCity={currentCity}
            pageCity={pageCity}
          />
        )}
      </div>
    </div>
  );
};

const RecommendedCities = ({
  regionGroups = [],
  onCityClick = noop,
  currentCity = null,
  pageCity = null,
}) => {
  const { t } = useTranslation();
  const links = regionGroups.map(([letter]) => letter.toUpperCase());
  const quickJumpLinks = (letter) => {
    const element = document.getElementById(`group-${letter}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  return (
    <div className="ui-region-picker-mobile__recommendedCities">
      {!!currentCity && (
        <div className="recommendedCities-location">
          <LocationIcon size={24}></LocationIcon>
          <span onClick={() => onCityClick(currentCity)}>
            {currentCity?.name}
          </span>
        </div>
      )}
      <div className="recommendedCities-title">
        {t("Header.Recommended cities")}
      </div>
      <div className="recommendedCities-links">
        {links.map((letter) => {
          return (
            <span
              key={letter}
              className="recommendedCities-link"
              onClick={() => quickJumpLinks(letter)}
            >
              {letter}
            </span>
          );
        })}
      </div>
      {regionGroups.map(([letter, cities]) => (
        <div
          key={letter}
          className="recommendedCities-group"
          role="listitem"
          id={`group-${letter}`}
        >
          <div className="recommendedCities-groupLetter">{letter}</div>
          <div className="recommendedCities-cities">
            {(cities || []).map((city) => (
              <div
                key={city.id}
                className="recommendedCities-city"
                onClick={() => onCityClick(city)}
              >
                {city.name}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const SearchResult = ({
  searchResults = [],
  onCityClick = noop,
  loading,
  isEmpty,
}) => {
  const { t } = useTranslation();
  return (
    <div className="ui-region-picker-mobile__searchResults">
      <div className="searchResults-title">{t("Header.SearchResults")}</div>
      {loading && (
        <div className="searchResults-loading">
          <DcLoading red={true}></DcLoading>
        </div>
      )}
      <div className="searchResults-list">
        {searchResults.map((item) => {
          const id = typeof item === "string" ? item : item.id;
          return (
            <div
              key={id}
              className="searchResults-item"
              onClick={() => onCityClick(item)}
            >
              {item.name},{item.country_title}
            </div>
          );
        })}
        {isEmpty && (
          <div className="ui-head-bar__keywordEmpty" role="status">
            <EmptyIcon />
            <p> {t("Header.No results found")} </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(MobileRegionPicker);
